import React, { useMemo } from 'react';
import './rule-content-zone.scss';
import { EditableRule, MultiSelectType } from '../../../../../model/EditableRule';
import EntitySelector, { DefaultLabel } from '../../../../shared/entity-selector/entity-selector';
import FormField from '../../../../shared/edit-row-modal/form-field/form-field';
import { Select } from 'antd';
import { getEnumOptions, Zone } from '../../../../../utils/enums';
import { useStores } from '../../../../store';
import { AddressGroup } from '../../../../../dtos/version.dto';
import { observer } from 'mobx-react-lite';
import CountryItem from './country-item';
import { useGenericHandler } from '../useGenericandler';
import { AddressGroupInfoTooltip } from '../../../inventory/addresses/address-group-info-tooltip';
import useCustomModal from '../../../../shared/custom-modal/custom-modal';
import { useAddressGroupsColumns } from '../../../inventory/groups/address-groups-columns-config';
import { useAddressGroupsCrudConfig } from '../../../inventory/groups/address-groups-crud-config';
import { FieldMessage, getMandatoryErrorMessage } from '../../../../../dtos/error-message.dto';
import { InfoAgMsg } from './info-msg-ag';
import Arrow from '@svg/chevron-down_20.svg?react';
import { SystemConfiguration } from '../../../../../utils/system-configuration';
import { RuleHelper, RuleNameAlias } from '../../../../../utils/rule-helper';

interface RuleEditorZoneProps {
    ruleData: EditableRule;
    sectionErrors: {[field: string]: FieldMessage};
    handleChange: (name: any, value: any) => void;
    handleRuleChange: (updatedRule: Partial<EditableRule>) => void;
    type: 'src' | 'dst';
}

const RuleEditorZone: React.FC<RuleEditorZoneProps> = observer(({
                                                                  ruleData,
                                                                  sectionErrors,
                                                                  handleChange,
                                                                 handleRuleChange,
                                                                 type
                                                             }) => {

    const [showModal,, contextHolder] = useCustomModal();

    const { fwVersionStore } = useStores();
    const oppositeType = type === 'src' ? 'dst' : 'src';

    const fullAddressGroupsList: AddressGroup[] = useMemo(() =>
            fwVersionStore.selectedFwVersion?.content.policy.address_groups || [],
        [fwVersionStore.selectedFwVersion?.content.policy.address_groups]);

    const addressGroupsHandler = useGenericHandler(
        fullAddressGroupsList,
        ruleData[`${type}AddressGroups`],
        'id',
        `${type}AddressGroups`,
        handleRuleChange
    );

    const fullCountriesList = SystemConfiguration.getInstance().countryDtoMap ?
        Object.values(SystemConfiguration.getInstance().countryDtoMap).flat() :
        [];

    const regionsHandler = useGenericHandler(
        fullCountriesList,
        ruleData[`${type}Regions`],
        'countryCode',
        `${type}Regions`,
        handleRuleChange
    );

    const zoneDependenciesUpdate = (zone: Zone) => {
        const updatedData: Partial<EditableRule> = {};
        const oppositeValue = zone === Zone.INTERNET ? Zone.MY_NETWORK : Zone.INTERNET;
        updatedData[`${type}Zone`] = zone;
        updatedData[`${oppositeType}Zone`] = oppositeValue;
        updatedData[`srcAddressGroups`] = [];
        updatedData[`srcAddressGroupsType`] = MultiSelectType.ANY;
        updatedData[`srcRegions`] = [];
        updatedData[`srcRegionsType`] = MultiSelectType.ANY;
        updatedData[`dstAddressGroups`] = [];
        updatedData[`dstAddressGroupsType`] = MultiSelectType.ANY;
        updatedData[`dstRegions`] = [];
        updatedData[`dstRegionsType`] = MultiSelectType.ANY;
        handleRuleChange(updatedData);
    };

    const changeZone = (value: Zone) => {

        if (!ruleData[`${type}Zone`]) {
            zoneDependenciesUpdate(value);
        }
        else {
            showModal({
                wrapClassName: 'change-zone-modal-wrapper',
                width: 480,
                title: 'Warning',
                body: () => <>
                    <div className="modal-commit-message">
                        Changing the Source Zone will reset both Source and Destination configuration.
                    </div>
                    <div className="modal-commit-message">
                        Would you like to continue?
                    </div>
                </>,
                okText: 'Continue',
                cancelText: 'Cancel',
                onOk: () => zoneDependenciesUpdate(value),
                closable: false
            });
        }
    };

    const isLocationRule = type === 'src' ?
        RuleHelper.getRuleName(RuleNameAlias.GEO_IP_SRC) ===  ruleData.name :
        RuleHelper.getRuleName(RuleNameAlias.GEO_IP_DEST) === ruleData.name;

    return (
        <div className={'rule-editor-src'}>
            <FormField label={'Zone'} errorMessage={getMandatoryErrorMessage(sectionErrors[`${type}Zone`])}>
                <Select
                    disabled={ruleData.isDefault}
                    value={ruleData[`${type}Zone`]}
                    placeholder={'Select'}
                    onChange={changeZone}
                    options={getEnumOptions(Zone)}
                    style={{ width: 130 }}
                    popupClassName="dropdown-custom"
                    suffixIcon={<Arrow />}
                />
            </FormField>
            <div className={'rule-editor-src-locations'}>
                <EntitySelector
                    disabled={ruleData.isDefault || !ruleData[`${type}Zone`]}
                    entityName={{ singular: 'Address Group', plural: 'Address Groups' }}
                    fullList={fullAddressGroupsList.map(_ => { return { name: _.displayName, key: _.id, data: _ };}) || []}
                    selectedItems={ruleData[`${type}AddressGroups`]}
                    label="Traffic"
                    handleSelection={addressGroupsHandler.handleItemChange}
                    handleBulkAllSelection={addressGroupsHandler.handleAllItemsChange}
                    handleTypeSelection={addressGroupsHandler.handleTypeChange}
                    type={ruleData[`${type}AddressGroupsType`]}
                    EntityLabel={({data}) => <DefaultLabel label={data.displayName} />}
                    EntityInfo={({data}) => <AddressGroupInfoTooltip displayName={data.displayName} />}
                    handleRemove={addressGroupsHandler.removeItem}
                    error={sectionErrors[`${type}AddressGroupsType`]}
                    columnsConfig={useAddressGroupsColumns()}
                    crudConfig={useAddressGroupsCrudConfig()}
                    InfoMsg={(data) => <InfoAgMsg ag={data} />}
                />

                <EntitySelector
                    disabled={(ruleData.isDefault && !isLocationRule) || !ruleData[`${type}Zone`]}
                    entityName={{ singular: 'Region', plural: 'Regions' }}
                    fullList={fullCountriesList.map(_ => { return { name: _.countryName, key: _.countryCode, data: _ };}) || []}
                    selectedItems={ruleData[`${type}Regions`]}
                    label="Geo - Location"
                    type={ruleData[`${type}RegionsType`]}
                    handleSelection={regionsHandler.handleItemChange}
                    handleBulkAllSelection={regionsHandler.handleAllItemsChange}
                    handleTypeSelection={regionsHandler.handleTypeChange}
                    EntityLabel={CountryItem}
                    handleRemove={regionsHandler.removeItem}
                    error={sectionErrors[`${type}RegionsType`]}
                />
            </div>
            {contextHolder}
        </div>
    );
});

export default RuleEditorZone;